import {
  Badge,
  Box,
  Checkbox,
  FormControl,
  FormGroup,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  Typography,
} from '@mui/material';
import CustomAlert from 'components/shared/CustomAlert/CustomAlert';
import CustomButton from 'components/shared/CustomButton/CustomButton';
import CustomDateRangePicker from 'components/shared/CustomDateRangePicker/CustomDateRangePicker';
import CustomPopOver from 'components/shared/CustomPopOver/CustomPopOver';
import CustomSearch from 'components/shared/CustomSearch/CustomSearch';
import CustomTab from 'components/shared/CustomTab/CustomTab';
import { LightTooltip } from 'components/shared/CustomTooltip/CustomTooltip';
import { initialAlertState, useAlertStatus } from 'customHook/useAlertStatus';
import { Dayjs } from 'dayjs';
import React, { SyntheticEvent, useEffect, useState } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import {
  deleteLead,
  getAgencyDropdown,
  getAllLeads,
  getAssigneeDropdown,
  setOpenAddLeadDrawer,
  updateLead,
} from 'store/slices/leadsSlice';
import zeplinColor from 'theme/Color';
import AddLeadDrawer from './AddLeadDrawer';
import style from './LeadsComponent.module.css';
import { Lead, LeadKanbanColumn, LeadKanbanColumnId, LeadsKanbanColumn } from './LeadsKanbanColumn';
import { LeadsKanbanTable } from './LeadsKanbanTable/LeadsKanbanTable';
import NewLeadType from './NewLeadType/NewLeadType';
import CustomViewLead from './CustomViewLead/CustomViewLead';

const LeadKanbanColumns: LeadKanbanColumn[] = [
  {
    label: 'Open',
    color: zeplinColor.Neutral8,
    id: 'open',
  },
  {
    label: 'Contacted',
    color: zeplinColor.AlertInfo,
    id: 'contacted',
  },
  {
    label: 'No Response',
    color: zeplinColor.Blue,
    id: 'no-response',
  },
  {
    label: 'Responded',
    color: zeplinColor.Success,
    id: 'responded',
  },
  {
    label: 'Qualified',
    color: zeplinColor.Success,
    id: 'qualified',
  },
  {
    label: 'Unqualified',
    color: zeplinColor.Neutral4,
    id: 'unqualified',
  },
];

const LeadsComponent = () => {
  const dispatch = useAppDispatch();
  const {
    leads: allLeads,
    loading,
    error,
    totalCount,
    agencyDropdown,
    assigneeDropdown,
    openAddLeadDrawer,
    editLead,
  } = useAppSelector((state) => state.leads);
  const { alertState, completeOperation, setAlertState, getAlertContent } = useAlertStatus();

  const [value, setValue] = useState(0);
  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);
  const [layout, setLayout] = useState<'column' | 'row'>('column');
  const [addLeadDrawerOpen, setAddLeadDrawerOpen] = useState(false);
  const [selectedPipelines, setSelectedPipelines] = useState<string[]>([]);
  const [leads, setLeads] = useState<Record<LeadKanbanColumnId, Lead[]>>({
    open: [],
    contacted: [],
    'no-response': [],
    responded: [],
    qualified: [],
    unqualified: [],
  });
  const [kanbanLeads, setKanbanLeads] = useState<Lead[]>([]);
  const [kanbanPage, setKanbanPage] = useState(1);
  const [kanbanHasMore, setKanbanHasMore] = useState(true);

  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState<string[]>([]);
  const [sortOrder, setSortOrder] = useState<('asc' | 'desc')[]>([]);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(0);

  const [leadTypeModalOpen, setLeadTypeModalOpen] = useState(false);
  const [selectedLeadType, setSelectedLeadType] = useState('');
  const [selectedLeadTypeId, setSelectedLeadTypeId] = useState('');

  const [viewLeadDrawerOpen, setViewLeadDrawerOpen] = useState(false);
  const [selectedLead, setSelectedLead] = useState<Lead | null>(null);

  const KANBAN_LIMIT = 50;

  const [selectedFilters, setSelectedFilters] = useState<{
    assignee: string[];
    agency: string[];
    startDate: Dayjs | null;
    closeDate: Dayjs | null;
  }>({
    assignee: [],
    agency: [],
    startDate: null,
    closeDate: null,
  });

  const fetchKanbanLeads = async (pageToFetch: number, reset: boolean = false) => {
    const sortString =
      sortBy.length > 0
        ? sortBy.map((field, index) => `${field}:${sortOrder[index] || 'asc'}`).join(',')
        : '';

    try {
      const response = await dispatch(
        getAllLeads({
          page: pageToFetch,
          limit: KANBAN_LIMIT,
          mine: value === 0,
          search: searchTerm,
          sport: selectedPipelines.join(','),
          sort: sortString,
        }),
      ).unwrap();

      const data = response?.data || [];
      const total = response?.total || 0;

      setKanbanPage(pageToFetch);
      setKanbanHasMore(pageToFetch * KANBAN_LIMIT < total);
    } catch (err) {
      setKanbanHasMore(false);
    }
  };

  useEffect(() => {
    if (layout !== 'row') return;
    const fetchLeads = async () => {
      const sortString =
        sortBy.length > 0
          ? sortBy.map((field, index) => `${field}:${sortOrder[index] || 'asc'}`).join(',')
          : '';

      const limit = rowsPerPage;
      const page = currentPage + 1; // API expects 1-based page numbers

      await dispatch(
        getAllLeads({
          page,
          limit,
          mine: value === 0,
          search: searchTerm,
          sport: selectedPipelines.join(','),
          sort: sortString,
        }),
      );
    };
    fetchLeads();
  }, [
    dispatch,
    value,
    searchTerm,
    selectedPipelines,
    sortBy,
    sortOrder,
    layout,
    rowsPerPage,
    currentPage,
  ]);

  useEffect(() => {
    if (layout !== 'column') return;
    setKanbanLeads([]);
    setKanbanPage(1);
    setKanbanHasMore(true);
    fetchKanbanLeads(1, true);
  }, [dispatch, value, searchTerm, selectedPipelines, sortBy, sortOrder, layout]);

  useEffect(() => {
    if (loading) {
      return;
    }
    const leadsSource = allLeads;
    // Group leads by status into kanban columns
    const groupedLeads: Record<LeadKanbanColumnId, Lead[]> = {
      open: [],
      contacted: [],
      'no-response': [],
      responded: [],
      qualified: [],
      unqualified: [],
    };

    leadsSource.forEach((lead) => {
      const status = lead.status?.toLowerCase().replace(/\s+/g, '-') || 'open';

      if (status === 'open' || status === 'new') {
        groupedLeads.open.push(lead);
      } else if (status === 'contacted') {
        groupedLeads.contacted.push(lead);
      } else if (status === 'no-response' || status === 'no response') {
        groupedLeads['no-response'].push(lead);
      } else if (status === 'responded') {
        groupedLeads.responded.push(lead);
      } else if (status === 'qualified') {
        groupedLeads.qualified.push(lead);
      } else if (status === 'unqualified') {
        groupedLeads.unqualified.push(lead);
      } else {
        groupedLeads.open.push(lead);
      }
    });
    setLeads(groupedLeads);
  }, [allLeads, layout]);

  // Monitor for API errors and show alerts
  useEffect(() => {
    if (error) {
      completeOperation('update', error, true);
    }
  }, [error, completeOperation]);
  useEffect(() => {
    if (!selectedLead?.leadId) return;
    const latestSelected = allLeads.find((lead) => lead.leadId === selectedLead.leadId);
    if (latestSelected) {
      setSelectedLead(latestSelected);
    }
  }, [allLeads, selectedLead?.leadId]);

  const handleSearchUsers = (searchTerm: string) => {
    setSearchTerm(searchTerm);
  };
  const handlePipelineChange = (value: string | string[]) => {
    setSelectedPipelines(Array.isArray(value) ? value : [value]);
  };
  const handleChange = (_: SyntheticEvent, newValue: number) => {
    sessionStorage.setItem('LeadsTab', newValue.toString());
    setValue(newValue);
  };
  const handleClearAllFilters = () => {
    setSelectedFilters({
      assignee: [],
      agency: [],
      closeDate: null,
      startDate: null,
    });
    fetchKanbanLeads(1, true);
    setAnchorEl(null);
  };
  const Tabs = [
    {
      label: 'My Leads',
      id: 0,
      isHidden: value !== 0,
      Component: <></>,
    },
    {
      label: 'All Leads',
      id: 1,
      isHidden: value !== 1,
      Component: <></>,
    },
  ];

  const handleClick = (event: React.MouseEvent<HTMLDivElement>) => {
    dispatch(getAgencyDropdown());
    dispatch(getAssigneeDropdown());
    setAnchorEl(event.currentTarget);
  };
  const handleLeadCreateClick = (event: React.MouseEvent<HTMLDivElement>) => {
    setLeadTypeModalOpen(true);
  };
  const handleLeadTypeModalClose = () => {
    setLeadTypeModalOpen(false);
    setSelectedLeadType('');
    setSelectedLeadTypeId('');
  };

  const handleContinueToDrawer = () => {
    if (!selectedLeadTypeId) return;
    setLeadTypeModalOpen(false);
    dispatch(setOpenAddLeadDrawer(true));
  };
  const applyFilter = () => {
    const sortString =
      sortBy.length > 0
        ? sortBy.map((field, index) => `${field}:${sortOrder[index] || 'asc'}`).join(',')
        : '';
    const start = selectedFilters?.startDate?.format('YYYY-MM-DD');
    const end = selectedFilters?.closeDate?.format('YYYY-MM-DD');
    dispatch(
      getAllLeads({
        page: 1,
        limit: KANBAN_LIMIT,
        mine: value === 0,
        search: searchTerm,
        sport: selectedPipelines.join(','),
        sort: sortString,
        ...(start && end
          ? { lastModifiedFrom: start, lastModifiedTo: end }
          : { lastModifiedDate: start || end || undefined }),
        assignedTo: selectedFilters.assignee.join(',') || undefined,
        agencyName: selectedFilters.agency.join(',') || undefined,
      }),
    );
    setAnchorEl(null);
  };
  const handleFilterChange = (
    filterType: 'assignee' | 'agency' | 'date',
    value: string | string[],
  ) => {
    setSelectedFilters((prev) => ({
      ...prev,
      [filterType]: Array.isArray(value) ? value : [value],
    }));
  };

  const handleOnDrag = (e: React.DragEvent, leadId: string, sourceColumn: LeadKanbanColumnId) => {
    e.dataTransfer?.setData('leadId', leadId);
    e.dataTransfer?.setData('sourceColumn', sourceColumn);
  };

  const handleOnDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const handleOnDrop = (e: React.DragEvent, targetColumn: LeadKanbanColumnId) => {
    const leadId = e.dataTransfer.getData('leadId');
    const sourceColumn = e.dataTransfer.getData('sourceColumn') as LeadKanbanColumnId;

    if (!leadId || !sourceColumn || sourceColumn === targetColumn) return;

    setLeads((prev) => {
      const leadToMove = prev[sourceColumn].find((lead) => lead.leadId === leadId);
      if (!leadToMove) return prev;

      const newSourceList = prev[sourceColumn].filter((lead) => lead.leadId !== leadId);
      const newTargetList = [...prev[targetColumn], leadToMove];

      return {
        ...prev,
        [sourceColumn]: newSourceList,
        [targetColumn]: newTargetList,
      };
    });

    // Update lead status via API
    const sortString =
      sortBy.length > 0
        ? sortBy.map((field, index) => `${field}:${sortOrder[index] || 'asc'}`).join(',')
        : '';
    const limit = layout === 'row' ? rowsPerPage : 50;
    const page = layout === 'row' ? currentPage + 1 : 1;

    dispatch(
      updateLead({
        leadId,
        data: { status: targetColumn },
        page,
        limit,
        mine: value === 0,
        search: searchTerm,
        sport: selectedPipelines.join(','),
        sort: sortString,
      }),
    );
  };

  const handleStatusChange = (leadId: string, status: LeadKanbanColumnId) => {
    const sortString =
      sortBy.length > 0
        ? sortBy.map((field, index) => `${field}:${sortOrder[index] || 'asc'}`).join(',')
        : '';
    const limit = layout === 'row' ? rowsPerPage : 50;
    const page = layout === 'row' ? currentPage + 1 : 1;

    dispatch(
      updateLead({
        leadId,
        data: { status },
        page,
        limit,
        mine: value === 0,
        search: searchTerm,
        sport: selectedPipelines.join(','),
        sort: sortString,
      }),
    )
      .unwrap()
      .then(() => {
        completeOperation('update', 'Lead status updated successfully');
      })
      .catch((err) => {
        completeOperation('update', 'Failed to update lead status', true);
      });
  };

  const handleViewDetails = (lead: Lead) => {
    setSelectedLead(lead);
    setViewLeadDrawerOpen(true);
  };

  const handleDeleteLead = (leadId: string, onComplete?: () => void) => {
    const sortString =
      sortBy.length > 0
        ? sortBy.map((field, index) => `${field}:${sortOrder[index] || 'asc'}`).join(',')
        : '';
    const limit = layout === 'row' ? rowsPerPage : 50;
    const page = layout === 'row' ? currentPage + 1 : 1;

    dispatch(
      deleteLead({
        leadId,
        page,
        limit,
        mine: value === 0,
        search: searchTerm,
        sport: selectedPipelines.join(','),
        sort: sortString,
      }),
    )
      .unwrap()
      .then(() => {
        completeOperation('delete', 'Lead deleted successfully');
        if (onComplete) {
          onComplete();
        }
      })
      .catch((err) => {
        completeOperation('delete', 'Failed to delete lead', true);
      });
  };

  const handleSort = (field: string, order: 'asc' | 'desc') => {
    setSortBy([field]);
    setSortOrder([order]);
  };

  const hasActiveFilters =
    selectedFilters.assignee.length > 0 ||
    selectedFilters.agency.length > 0 ||
    selectedFilters.startDate !== null ||
    selectedFilters.closeDate !== null;
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      <Box
        sx={{
          padding: { xs: '20px 20px', xl: '24px 24px' },
          display: 'flex',
          justifyContent: 'space-between',
        }}
      >
        <CustomTab tabs={Tabs} handleChange={handleChange} value={value} />
        <Box sx={{ display: 'flex', gap: '12px' }}>
          <Box sx={{ flex: 1, width: '30rem' }}>
            <CustomSearch getProjects={handleSearchUsers} />
          </Box>
          <Box sx={{ width: { xs: '200px', xl: '260px' } }}>
            <FormControl fullWidth sx={{ '.MuiInputBase-formControl': { borderRadius: '50px' } }}>
              <InputLabel
                id='pipeline-type-label'
                sx={{
                  fontSize: { xs: '14px', xl: '16px' },
                  '&.Mui-focused': { color: zeplinColor.BasicBlack },
                }}
              >
                Select Pipeline Type
              </InputLabel>
              <Select
                labelId='pipeline-type-label'
                id='pipeline-type'
                multiple
                value={selectedPipelines}
                onChange={(e) => handlePipelineChange(e.target.value)}
                sx={{
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderWidth: '1px',
                    color: zeplinColor.Neutral6,
                    border: `1px solid ${zeplinColor.Neutral6}`,
                    borderRadius: '50px',
                  },
                  height: '48px',
                }}
                input={
                  <OutlinedInput
                    sx={{
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderWidth: '1px',
                        color: zeplinColor.Neutral6,
                        border: `1px solid ${zeplinColor.Neutral6}`,
                      },
                      borderRadius: '50px',
                      border: `1px solid ${zeplinColor.Neutral6}`,
                      height: '48px',
                      padding: '0px 14px',
                      display: 'flex',
                      alignItems: 'center',
                    }}
                    label='Select Pipeline Type'
                  />
                }
                renderValue={(selected) => (selected as string[]).join(', ')}
                IconComponent={() => (
                  <img src='/assets/down-arrow.svg' style={{ width: '24px', height: '24px' }} />
                )}
              >
                {['Basketball', 'Football', 'Soccer', 'Golf', 'Hockey', 'Tennis'].map((label) => (
                  <MenuItem
                    key={label}
                    value={label}
                    sx={{
                      fontSize: { xs: '14px', xl: '16px' },
                      padding: { xs: '6px 16px', xl: '8px 16px' },
                    }}
                  >
                    <Checkbox
                      checked={selectedPipelines.indexOf(label) > -1}
                      sx={{
                        color: zeplinColor.BasicBlack,
                        '&.Mui-checked': { color: zeplinColor.BasicBlack },
                      }}
                    />
                    {label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          <Box sx={{ display: 'flex', gap: '16px' }}>
            <Badge
              badgeContent={0}
              color='primary'
              sx={{ '.MuiBadge-badge': { top: '0.7rem', right: '0.2rem' } }}
            >
              <LightTooltip
                title={
                  <Typography variant='body3' color={zeplinColor.Neutral1}>
                    Filter
                  </Typography>
                }
                placement='bottom-end'
                style={{ backgroundColor: zeplinColor.BasicBlack, borderRadius: '8px' }}
                // sx={{ [`& .${tooltipClasses.tooltip}`]: { backgroundColor: zeplinColor.Neutral1 } }}
              >
                <Box
                  sx={{
                    borderRadius: '50px',
                    width: { xs: '30px', xl: '50px' },
                    height: { xs: '30px', xl: '50px' },
                    border: `1px solid ${zeplinColor.Neutral6}`,
                    padding: '0px 10px',
                    display: 'flex',
                    gap: 8,
                    justifyContent: 'center',
                    alignItems: 'center',
                    cursor: 'pointer',
                    //   flex: 1,
                  }}
                  onClick={handleClick}
                >
                  <img src={'/assets/filter.svg'} className={style.filterIcon} />
                </Box>
              </LightTooltip>
            </Badge>
            <Box
              sx={{
                borderRadius: '50px',
                border: `1px solid ${zeplinColor.Neutral6}`,
                padding: '6px',
                width: { xs: '82px', xl: '92px' },
                height: { xs: '40px', xl: '48px' },
                display: 'flex',
                justifyContent: 'space-around',
                alignItems: 'center',
                backgroundColor: zeplinColor.Neutral1,
                position: 'relative', // Important for absolute positioning of slider
              }}
            >
              {/* Animated slider background */}
              <Box
                sx={{
                  position: 'absolute',
                  width: { xs: '35px', xl: '40px' },
                  height: { xs: '28px', xl: '36px' },
                  backgroundColor: zeplinColor.Brand1,
                  opacity: 0.1,
                  borderRadius: '40px',
                  transition: 'transform 0.3s ease',
                  transform:
                    layout === 'column'
                      ? 'translateX(0)'
                      : { xs: 'translateX(35px)', xl: 'translateX(40px)' },
                  left: '6px',
                }}
              />

              {/* Column icon */}
              <Box
                onClick={() => setLayout('column')}
                sx={{
                  zIndex: 1,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  width: '40px',
                  height: '100%',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                }}
              >
                <img
                  src={
                    layout === 'column'
                      ? `/assets/selected-col-layout.svg`
                      : `/assets/column-layout-icon.svg`
                  }
                  className={layout === 'column' ? style['icons'] : style['selected-icons']}
                  alt='Column layout'
                  style={{
                    transition: 'all 0.2s ease',
                    transform: layout === 'column' ? 'scale(1.1)' : 'scale(1)',
                  }}
                />
              </Box>

              {/* Row icon */}
              <Box
                onClick={() => setLayout('row')}
                sx={{
                  zIndex: 1,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  width: '40px',
                  height: '100%',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                }}
              >
                <img
                  src={
                    layout === 'row'
                      ? `/assets/selected-row-layout.svg`
                      : `/assets/row-layout-icon.svg`
                  }
                  className={layout === 'row' ? style['icons'] : style['selected-icons']}
                  alt='Row layout'
                  style={{
                    transition: 'all 0.2s ease',
                    transform: layout === 'row' ? 'scale(1.1)' : 'scale(1)',
                  }}
                />
              </Box>
            </Box>
            <LightTooltip
              title={
                <Typography variant='responsiveBody3' color={zeplinColor.Neutral1}>
                  New Lead
                </Typography>
              }
              placement='bottom-end'
              style={{ backgroundColor: zeplinColor.BasicBlack, borderRadius: '8px' }}
            >
              <CustomButton
                disabled={false}
                onClick={handleLeadCreateClick}
                variant='contained'
                color={'primary'}
                buttonVariant='responsiveButtonLarge'
                style={{
                  width: 'fit-content',
                  borderRadius: { xs: '10px', xl: '14px' },
                  height: { xs: 30, xl: 48 },
                  padding: { xs: '10px 12px', xl: '14px 16px' },
                  '&.Mui-focusVisible': { backgroundColor: '#333CAA' },
                }}
                textStyle={{ color: zeplinColor.Neutral1, width: 'fit-content' }}
                text={'New Lead'}
                iconPosition='start'
                icon={<img src={`/assets/add-white.svg`} className={style['add-icon']} />}
                // tabindex={4}
              />
            </LightTooltip>
            <CustomPopOver
              setAnchorEl={setAnchorEl}
              anchorEl={anchorEl}
              id={'simple-popover'}
              sx={{
                borderRadius: '14px',
                boxShadow: 'rgba(0, 0, 0, 0.15)',
                marginTop: '8px',
                marginRight: '2rem',
                marginLeft: '-4rem',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <Box
                  sx={{
                    padding: '16px',
                    width: { xs: '250px', xl: '283px' },
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '24px',
                    height: '100%',
                    overflow: 'auto',
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant='responsiveHeading3' color={zeplinColor.BasicBlack}>
                      Filter
                    </Typography>
                    <Typography
                      variant='responsiveButtonSmall'
                      color={hasActiveFilters ? zeplinColor.Brand1 : zeplinColor.Neutral8}
                      onClick={hasActiveFilters ? handleClearAllFilters : undefined}
                      sx={{
                        cursor: hasActiveFilters ? 'pointer' : 'default',
                        display: 'flex',
                        alignItems: 'center',
                      }}
                    >
                      Clear All
                    </Typography>
                  </Box>

                  <FormGroup sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                    {/* Assignee Filter */}
                    <FormControl sx={{ width: '100%' }}>
                      <InputLabel
                        id='assignee-filter-label'
                        sx={{
                          fontSize: { xs: '14px', xl: '16px' },
                          '&.Mui-focused': { color: zeplinColor.Neutral9 },
                        }}
                      >
                        Assignee
                      </InputLabel>
                      <Select
                        labelId='assignee-filter-label'
                        multiple
                        value={selectedFilters.assignee}
                        onChange={(e) => handleFilterChange('assignee', e.target.value)}
                        renderValue={(selected) => (selected as string[]).join(', ')}
                        input={
                          <OutlinedInput
                            sx={{
                              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                borderWidth: '1px',
                                color: zeplinColor.Neutral9,
                                borderColor: zeplinColor.Neutral9,
                              },
                              // paddingLeft: '16px',
                              padding: '0px 14px',
                            }}
                            label='Assignee'
                          />
                        }
                        IconComponent={() => (
                          <img src='/assets/down-arrow.svg' className={style['icon-24']} />
                        )}
                      >
                        {assigneeDropdown.map((assignee) => (
                          <MenuItem key={assignee.Name} value={assignee?.Name}>
                            <Checkbox
                              checked={selectedFilters.assignee.indexOf(assignee?.Name) > -1}
                            />
                            {assignee?.Name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    <FormControl sx={{ width: '100%' }}>
                      <InputLabel
                        id='agency-filter-label'
                        sx={{
                          fontSize: { xs: '14px', xl: '16px' },
                          '&.Mui-focused': { color: zeplinColor.Neutral9 },
                        }}
                      >
                        Agency
                      </InputLabel>
                      <Select
                        labelId='agency-filter-label'
                        multiple
                        value={selectedFilters.agency}
                        onChange={(e) => handleFilterChange('agency', e.target.value)}
                        renderValue={(selected) => (selected as string[]).join(', ')}
                        input={
                          <OutlinedInput
                            sx={{
                              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                borderWidth: '1px',
                                color: zeplinColor.Neutral9,
                                borderColor: zeplinColor.Neutral9,
                              },
                              // paddingLeft: '16px',
                              padding: '0px 14px',
                            }}
                            label='Agency'
                          />
                        }
                        IconComponent={() => (
                          <img src='/assets/down-arrow.svg' className={style['icon-24']} />
                        )}
                      >
                        {agencyDropdown.map((agency) => (
                          <MenuItem key={agency} value={agency}>
                            <Checkbox checked={selectedFilters.agency.indexOf(agency) > -1} />
                            {agency}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    {/* Date Filter */}
                    <CustomDateRangePicker
                      startDate={selectedFilters.startDate}
                      closeDate={selectedFilters.closeDate}
                      onStartDateChange={(date) => {
                        setSelectedFilters((prev) => ({ ...prev, startDate: date }));
                      }}
                      onCloseDateChange={(date) => {
                        setSelectedFilters((prev) => ({ ...prev, closeDate: date }));
                      }}
                      label='Date Range'
                      placeholder='Select date range'
                      size='medium'
                    />
                  </FormGroup>

                  <Box sx={{ display: 'flex', alignItems: 'flex-end', flex: 1 }}>
                    <CustomButton
                      onClick={applyFilter}
                      variant='contained'
                      disabled={!hasActiveFilters}
                      color={'primary'}
                      style={{
                        width: '100%',
                        borderRadius: '14px',
                        height: { xs: '48px', md: '56px' },
                      }}
                      textStyle={{
                        color: !hasActiveFilters ? zeplinColor.Neutral8 : zeplinColor.Neutral1,
                        fontSize: { xs: '14px', xl: '16px' },
                      }}
                      text={'Apply'}
                    />
                  </Box>
                </Box>
              </Box>
            </CustomPopOver>
          </Box>
        </Box>
      </Box>

      {layout === 'column' && (
        <Box sx={{ padding: { xs: '0 20px 20px', xl: '0 24px 24px' } }}>
          <InfiniteScroll
            dataLength={kanbanLeads.length}
            next={() => fetchKanbanLeads(kanbanPage + 1)}
            hasMore={kanbanHasMore}
            loader={<Box sx={{ display: 'flex', justifyContent: 'center', padding: '16px' }}></Box>}
            height={'calc(100vh - 100px)'}
            style={{ display: 'flex', flexDirection: 'column', gap: '16px', width: '100%' }}
          >
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                width: '100%',
                gap: '24px',
                overflowX: 'auto',
              }}
            >
              {LeadKanbanColumns.map(({ id, label, color }) => (
                <LeadsKanbanColumn
                  key={id}
                  columnId={id}
                  label={label}
                  color={color}
                  leads={leads[id]}
                  isLoading={loading}
                  isDeleting={loading}
                  onDragOver={handleOnDragOver}
                  onDrop={handleOnDrop}
                  onDragStart={handleOnDrag}
                  onStatusChange={handleStatusChange}
                  onDelete={handleDeleteLead}
                  onViewDetails={handleViewDetails}
                />
              ))}
            </Box>
            {!loading && allLeads.length === 0 && (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center',
                  padding: '24px 0',
                  height: { xs: 'calc(100vh - 400px)', xl: 'calc(100vh - 300px)' },
                }}
              >
                <img src='/assets/leads-icon.svg' style={{ width: '88px', height: '88px' }} />
                <Typography
                  variant='responsiveHeading3'
                  color={zeplinColor.BasicBlack}
                  sx={{ marginTop: { xs: '22px', xl: '24px' } }}
                >
                  No Leads Yet
                </Typography>
                <Typography
                  variant='responsiveBody3'
                  color={zeplinColor.Neutral8}
                  sx={{ width: '70%', textAlign: 'center', marginTop: { xs: '10px', xl: '12px' } }}
                >
                  Looks like there are no leads here yet. New leads will appear once added.
                </Typography>
              </Box>
            )}
          </InfiniteScroll>
        </Box>
      )}

      {layout === 'row' && (
        <Box sx={{ padding: { xs: '0 20px 20px', xl: '0 24px 24px' } }}>
          <LeadsKanbanTable
            leads={allLeads}
            onStatusChange={handleStatusChange}
            onDelete={handleDeleteLead}
            onViewDetails={handleViewDetails}
            onSort={handleSort}
            sortBy={sortBy}
            sortOrder={sortOrder}
            rowsPerPage={rowsPerPage}
            setRowsPerPage={setRowsPerPage}
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
            totalCount={totalCount}
            isLoading={loading}
            isDeleting={loading}
          />
        </Box>
      )}

      <CustomAlert
        open={alertState.status !== 'idle'}
        handleClose={() => setAlertState(initialAlertState)}
        message={getAlertContent(alertState)}
        error={alertState.status === 'error'}
      />
      <NewLeadType
        modalOpen={leadTypeModalOpen}
        handleModalClose={handleLeadTypeModalClose}
        handleContinueAdd={handleContinueToDrawer}
        setTypeSelected={setSelectedLeadType}
        typeSelected={selectedLeadType}
        setTypeIdSelected={setSelectedLeadTypeId}
      />
      <AddLeadDrawer
        drawerState={openAddLeadDrawer}
        setDrawerState={(open) => dispatch(setOpenAddLeadDrawer(open))}
        leadType={selectedLeadType || editLead?.leadTypeName}
        leadTypeId={selectedLeadTypeId || editLead?.leadTypeId}
        onLeadAdded={() => {
          completeOperation('create', 'Lead created successfully');
          // Refresh leads after adding
          if (layout === 'column') {
            setKanbanLeads([]);
            setKanbanPage(1);
            setKanbanHasMore(true);
            fetchKanbanLeads(1, true);
          } else {
            dispatch(
              getAllLeads({
                page: currentPage + 1,
                limit: rowsPerPage,
                mine: value === 0,
                search: searchTerm,
                sport: selectedPipelines.join(','),
                sort:
                  sortBy.length > 0
                    ? sortBy
                        .map((field, index) => `${field}:${sortOrder[index] || 'asc'}`)
                        .join(',')
                    : '',
              }),
            );
          }
        }}
      />
      <CustomViewLead
        key={selectedLead?.leadId}
        drawerState={viewLeadDrawerOpen}
        setDrawerState={setViewLeadDrawerOpen}
        selectedData={selectedLead}
        setSelected={setSelectedLead}
        onStatusChange={handleStatusChange}
        onDelete={handleDeleteLead}
        onUpdate={async () => {
          await dispatch(
            getAllLeads({
              page: currentPage + 1,
              limit: KANBAN_LIMIT,
              mine: value === 0,
              search: searchTerm,
              sport: selectedPipelines.join(','),
              sort:
                sortBy.length > 0
                  ? sortBy.map((field, index) => `${field}:${sortOrder[index] || 'asc'}`).join(',')
                  : '',
            }),
          );
        }}
      />
      <CustomAlert
        open={alertState.status !== 'idle'}
        handleClose={() => setAlertState(initialAlertState)}
        message={getAlertContent(alertState)}
        error={alertState.status === 'error'}
      />
    </Box>
  );
};

export default LeadsComponent;
